import React, { useCallback, useEffect, useState } from 'react';
import { isMonthScopedView, useAppState } from '../../state/AppStateContext';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MonthStrip from './MonthStrip';
import StatusBar from './StatusBar';
import Toasts from './Toasts';
import CommandPalette from '../CommandPalette';
import DashboardView from '../../views/DashboardView';
import TransactionsView from '../../views/TransactionsView';
import BreakdownView from '../../views/BreakdownView';
import SubscriptionsView from '../../views/SubscriptionsView';
import CardsView from '../../views/CardsView';
import QuestsView from '../../views/QuestsView';
import AchievementsView from '../../views/AchievementsView';
import AddTransaction from '../../AddTransaction';
import ImportStatementModal from '../ImportStatementModal';

const AppShell = () => {
    const { view, loading, error, reload, refreshProgress, pushToast } = useAppState();
    const [adding, setAdding] = useState(false);
    // Holds the transaction being edited, or null for a new one. AddTransaction
    // switches itself into edit mode off this prop.
    const [editing, setEditing] = useState(null);
    const [importing, setImporting] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    const openAdd = useCallback(() => { setEditing(null); setAdding(true); }, []);
    const openEdit = useCallback((transaction) => { setEditing(transaction); setAdding(true); }, []);
    const closeForm = useCallback(() => { setAdding(false); setEditing(null); }, []);
    const openImport = useCallback(() => setImporting(true), []);

    const handleSaved = useCallback(async (saved, mode) => {
        closeForm();
        await reload();
        await refreshProgress();
        pushToast(
            mode === 'edit' ? 'Transaction updated' : 'Transaction saved',
            'Your totals have been updated',
            'xp'
        );
    }, [closeForm, reload, refreshProgress, pushToast]);

    // A warning means the rows were imported but a follow-up step (the card
    // statement summary, or pairing payments with checking) failed. A toast would
    // vanish before it could be read, so the modal stays open showing it.
    const handleImported = useCallback(async (imported, result = {}) => {
        const hasWarnings = Boolean(result.warnings?.length);
        if (!hasWarnings) setImporting(false);
        await reload();
        await refreshProgress();
        if (hasWarnings) return;
        const matched = result.transfersMatched;
        pushToast(
            'Statement imported',
            matched > 0
                ? `Matched ${matched} card ${matched === 1 ? 'payment' : 'payments'} to checking`
                : 'New transactions are in',
            'xp'
        );
    }, [reload, refreshProgress, pushToast]);

    // Shortcuts are ignored while typing so '/' and 'n' stay usable inside inputs.
    useEffect(() => {
        const onKeyDown = (event) => {
            const tag = document.activeElement?.tagName || '';
            const typing = /^(INPUT|SELECT|TEXTAREA)$/.test(tag) || document.activeElement?.isContentEditable;

            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setPaletteOpen(true);
                return;
            }
            if (event.key === 'Escape') {
                setPaletteOpen(false);
                return;
            }
            if (typing || paletteOpen || adding || importing) return;

            if (event.key === 'n' || event.key === 'N') {
                event.preventDefault();
                openAdd();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [paletteOpen, adding, importing, openAdd]);

    return (
        <div className="bq-shell">
            <Sidebar />

            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <TopBar onAdd={openAdd} onImport={openImport} onOpenPalette={() => setPaletteOpen(true)} />

                <main className="bq-main">
                    {isMonthScopedView(view) && <MonthStrip />}

                    {error && (
                        <div className="bq-empty" style={{ marginBottom: 14 }}>
                            <h3>Could not load your data</h3>
                            <p>{error}</p>
                            <button type="button" className="bq-btn bq-btn-p" onClick={reload}>Try again</button>
                        </div>
                    )}

                    {loading && !error && (
                        <div className="bq-panel"><div className="bq-pb" style={{ color: 'var(--ink-3)' }}>Loading…</div></div>
                    )}

                    {!loading && !error && (
                        <>
                            {view === 'dashboard' && <DashboardView onAdd={openAdd} />}
                            {view === 'transactions' && <TransactionsView onAdd={openAdd} onEdit={openEdit} />}
                            {view === 'breakdown' && <BreakdownView onAdd={openAdd} />}
                            {view === 'subscriptions' && <SubscriptionsView onAdd={openAdd} />}
                            {view === 'cards' && <CardsView onImport={openImport} />}
                            {view === 'quests' && <QuestsView />}
                            {view === 'achievements' && <AchievementsView />}
                        </>
                    )}
                </main>

                <StatusBar />
            </div>

            {adding && (
                <div className="bq-ov" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
                    <div
                        className="bq-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={editing ? 'Edit transaction' : 'Add transaction'}
                    >
                        <div style={{ padding: 16 }}>
                            <AddTransaction onSaved={handleSaved} onCancel={closeForm} editingTransaction={editing} />
                        </div>
                    </div>
                </div>
            )}

            {importing && (
                <ImportStatementModal onClose={() => setImporting(false)} onImported={handleImported} />
            )}

            <CommandPalette
                open={paletteOpen}
                onClose={() => setPaletteOpen(false)}
                onAdd={openAdd}
                onImport={openImport}
            />

            <Toasts />
        </div>
    );
};

export default AppShell;
